import { t } from '../i18n';
import { travellerName } from './traveller';
import type { Route } from '../backend/route';

/**
 * Keeps the Open Graph / Twitter tags in the live DOM in step with the route.
 *
 * This does **not** change what a link crawler unfurls. WhatsApp, iMessage,
 * Slack and friends fetch the URL with their own crawler and never execute the
 * bundle, so they always see the generic card baked into `index.html`. What
 * this covers is everything that reads the *loaded* document instead: the
 * `og:url` correction below (the static tag can't know the share code), and
 * any consumer — extensions, in-app browsers, WebKit's LinkPresentation — that
 * builds a preview from the live DOM rather than a fresh fetch.
 *
 * Personalising the first unfurl needs a server-side share-code lookup; see
 * README → "Share previews" for the endpoint that would do it.
 *
 * Emergencies never reach these tags. A share preview is cached by the
 * platform and rendered to everyone in the thread, so an alarming headline can
 * outlive the emergency it describes. Note this is a narrower guarantee than
 * it looks: `document.title` in `App.tsx` *does* carry emergency copy, on
 * purpose, so a watcher with the tab backgrounded still sees it — and some
 * share surfaces fall back to the title. The tags are the part we control.
 */

type MetaCopy = { title: string; description: string };

export function buildSocialMeta(route: Route | null): MetaCopy {
  const name = travellerName(route);

  if (!route || !name) {
    return {
      title: t('social.titleDefault'),
      description: t('social.descriptionDefault'),
    };
  }

  if (route.finished) {
    return {
      title: t('social.titleArrived', { name }),
      description: t('social.descriptionArrived', { name }),
    };
  }

  return {
    title: t('social.titleTravelling', { name }),
    description: t('social.descriptionTravelling', { name }),
  };
}

/**
 * Crawlers key off `property` for og:*, `name` for twitter:*. Writing the
 * wrong attribute is a silent no-op, so each tag is addressed the way its
 * spec expects rather than through one generic helper.
 */
function setMeta(attribute: 'property' | 'name', key: string, value: string): void {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

/**
 * The copy only changes at two moments in a journey — the name resolving and
 * `finished` flipping — but the caller re-runs on every location tick, which
 * on a long walk is hundreds of times. Skip the head writes when nothing
 * actually changed.
 */
let lastApplied: string | null = null;

/** Test seam — module state would otherwise leak between cases. */
export function resetSocialMetaCache(): void {
  lastApplied = null;
}

export function applySocialMeta(route: Route | null): void {
  const { title, description } = buildSocialMeta(route);
  // The share code is in the path, so the live URL is the one worth passing
  // on — index.html can't name it, and a crawler that trusted a bare-origin
  // og:url would send tappers to the no-share-code empty state.
  const url = window.location.href;

  const fingerprint = JSON.stringify([title, description, url]);
  if (fingerprint === lastApplied) return;
  lastApplied = fingerprint;

  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'description', description);
  setMeta('property', 'og:url', url);
}
